import type { MarketingRouterMatchInput } from '@luxuryescapes/contract-svc-promo/marketing-router/client';

/**
 * Turns a pasted customer-portal URL into the input the Marketing Router
 * evaluator sees for that visit. It mirrors how the portal derives the input
 * from a request (`www-le-customer` `toMarketingRouterUrlPath` and
 * `buildMarketingRouterMatchInput`): the region and locale segments come off
 * the front of the path, the UTMs come from the query string, and an `mrid` /
 * `mrvid` pair in the query is replayed as the visitor's sticky assignment.
 *
 * The region segment is required. The portal would fall back to the visitor's
 * cookie or location, which the operator cannot know, so a link without one
 * is rejected rather than guessed at.
 */

export const MARKETING_ROUTER_UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type MarketingRouterUtmField = (typeof MARKETING_ROUTER_UTM_FIELDS)[number];

export type ParsedMarketingRouterUrl = {
  /** Host of the pasted URL; null when only a path was pasted. */
  host: string | null;
  /** Region segment at the start of the path, upper-cased. */
  regionCode: string;
  /** Locale segment after the region (`en-AU`), when present. */
  locale: string | null;
  /** The page path as rules are authored: without region or locale. */
  path: string;
  utm: Record<MarketingRouterUtmField, string | null>;
  /** The assignment the URL advertises; the portal replays it before rolling again. */
  assignment: { mrid: string; mrvid: string } | null;
};

export type MarketingRouterUrlParseResult =
  | { status: 'ok'; parsed: ParsedMarketingRouterUrl }
  /** The text could not be read as a URL or path. */
  | { status: 'invalid-url' }
  /** The path does not start with a region segment. */
  | { status: 'missing-region' }
  /** The path starts with a two-letter segment that is not a region for this brand. */
  | { status: 'unknown-region'; segment: string };

// The portal treats the second path segment as a locale when it looks like
// `en-AU`, `en_AU` or `zh-Hant-TW`.
const LOCALE_SEGMENT = /^[a-z]{2}[-_](?:[A-Z][a-z]{3}[-_])?[A-Z]{2}$/;
const TWO_LETTER_SEGMENT = /^[a-z]{2}$/i;
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
// A bare path has no host; it is parsed against this origin and the host is discarded.
const PATH_ONLY_ORIGIN = 'https://path.invalid';

function readQueryValue(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim();
  return value ? value : null;
}

/** The text before the first `/`, `?` or `#`. */
function firstSegment(text: string): string {
  return text.split(/[/?#]/, 1)[0];
}

/**
 * Parses a full URL (`https://luxuryescapes.com/nz/hotels?utm_source=gha`),
 * a host without a scheme, or a path with query. A path may omit its leading
 * slash when it starts with a region (`au/hotels?utm_source=gha`).
 */
export function parseMarketingRouterUrl(
  raw: string,
  regionCodes: ReadonlyArray<string>,
): MarketingRouterUrlParseResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { status: 'invalid-url' };
  }

  const knownRegions = new Set(regionCodes.map((code) => code.toUpperCase()));
  const isRegionSegment = (segment: string) =>
    TWO_LETTER_SEGMENT.test(segment) && knownRegions.has(segment.toUpperCase());

  // `au/hotels` is a path that lost its leading slash, not a host called `au`.
  const isPathOnly =
    trimmed.startsWith('/') ||
    trimmed.startsWith('?') ||
    (!SCHEME.test(trimmed) && isRegionSegment(firstSegment(trimmed)));
  const href = isPathOnly
    ? `${PATH_ONLY_ORIGIN}${trimmed.startsWith('/') || trimmed.startsWith('?') ? '' : '/'}${trimmed}`
    : SCHEME.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { status: 'invalid-url' };
  }

  // "hotels" parses as a host called hotels; a real host has a dot in it.
  if (!isPathOnly && !url.hostname.includes('.') && url.hostname !== 'localhost') {
    return { status: 'invalid-url' };
  }

  const segments = url.pathname.split('/').filter(Boolean);

  if (!segments[0] || !TWO_LETTER_SEGMENT.test(segments[0])) {
    return { status: 'missing-region' };
  }
  const [regionSegment] = segments.splice(0, 1);
  if (!isRegionSegment(regionSegment)) {
    return { status: 'unknown-region', segment: regionSegment };
  }

  // A locale is only recognised directly after the region segment.
  let locale: string | null = null;
  if (segments[0] && LOCALE_SEGMENT.test(segments[0])) {
    [locale] = segments.splice(0, 1);
  }

  const utm = Object.fromEntries(
    MARKETING_ROUTER_UTM_FIELDS.map((field) => [field, readQueryValue(url.searchParams, field)]),
  ) as ParsedMarketingRouterUrl['utm'];

  const mrid = readQueryValue(url.searchParams, 'mrid');
  const mrvid = readQueryValue(url.searchParams, 'mrvid');

  return {
    status: 'ok',
    parsed: {
      host: isPathOnly ? null : url.host,
      regionCode: regionSegment.toUpperCase(),
      locale,
      path: `/${segments.join('/')}`,
      utm,
      assignment: mrid && mrvid ? { mrid, mrvid } : null,
    },
  };
}

/** The evaluator input for a parsed URL. */
export function toMarketingRouterMatchInput(
  parsed: ParsedMarketingRouterUrl,
  options: { evaluatedAt?: Date } = {},
): MarketingRouterMatchInput {
  const region = parsed.regionCode as MarketingRouterMatchInput['region'];

  return {
    region,
    utmSource: parsed.utm.utm_source,
    utmMedium: parsed.utm.utm_medium,
    utmCampaign: parsed.utm.utm_campaign,
    utmTerm: parsed.utm.utm_term,
    utmContent: parsed.utm.utm_content,
    url: parsed.path,
    evaluatedAt: options.evaluatedAt,
    // The URL carries no region for the assignment, so the portal takes the
    // advertised assignment to have been made in the current one.
    ...(parsed.assignment
      ? {
          existing: {
            previousInput: { region },
            existingVariants: { [parsed.assignment.mrid]: parsed.assignment.mrvid },
          },
        }
      : {}),
  };
}
